import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

type UserType = 'client' | 'practitioner';

const BASE_STORAGE_KEYS = {
  client: 'onboarding_completed_client',
  practitioner: 'onboarding_completed_practitioner',
} as const;

export const getOnboardingStorageKey = (userType: UserType, userId?: string) => {
  const baseKey = BASE_STORAGE_KEYS[userType];
  return userId ? `${baseKey}_${userId}` : baseKey;
};

export const useOnboarding = (userType: UserType, userId?: string) => {
  const [isRunning, setIsRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [dbChecked, setDbChecked] = useState(false);

  const storageKey = getOnboardingStorageKey(userType, userId);
  const tableName = userType === 'client' ? 'clients' : 'practitioners';

  // Check DB for completion status on mount
  useEffect(() => {
    if (!userId) return;

    const checkDb = async () => {
      try {
        const { data } = await supabase
          .from(tableName)
          .select('onboarding_completed')
          .eq('id', userId)
          .maybeSingle();

        const completed = (data as any)?.onboarding_completed ?? false;

        if (completed) {
          // Sync to localStorage for fast future checks
          localStorage.setItem(storageKey, 'true');
          setDbChecked(true);
          return;
        }

        // Fall back to localStorage (backward compat for users who completed before DB column existed)
        if (localStorage.getItem(storageKey) === 'true') {
          // Sync localStorage state to DB
          await supabase
            .from(tableName)
            .update({ onboarding_completed: true } as any)
            .eq('id', userId);
          setDbChecked(true);
          return;
        }

        // Not completed — allow auto-start
        setDbChecked(true);
      } catch (err) {
        console.error('Error checking onboarding status:', err);
        setDbChecked(true);
      }
    };

    checkDb();
  }, [userId, tableName, storageKey]);

  // Check if onboarding has been completed
  const isCompleted = useCallback(() => {
    return localStorage.getItem(storageKey) === 'true';
  }, [storageKey]);

  // Start the tour
  const startTour = useCallback(() => {
    setStepIndex(0);
    setIsRunning(true);
  }, []);

  // End the tour and mark as completed in both DB and localStorage
  const endTour = useCallback(async () => {
    setIsRunning(false);
    localStorage.setItem(storageKey, 'true');

    if (userId) {
      try {
        await supabase
          .from(tableName)
          .update({ onboarding_completed: true } as any)
          .eq('id', userId);
      } catch (err) {
        console.error('Error saving onboarding completion to DB:', err);
      }
    }
  }, [storageKey, userId, tableName]);

  // Reset the tour (for settings page)
  const resetTour = useCallback(async () => {
    localStorage.removeItem(storageKey);
    setStepIndex(0);
    setIsRunning(true);

    if (userId) {
      try {
        await supabase
          .from(tableName)
          .update({ onboarding_completed: false } as any)
          .eq('id', userId);
      } catch (err) {
        console.error('Error resetting onboarding in DB:', err);
      }
    }
  }, [storageKey, userId, tableName]);

  // Auto-start on first visit (only after DB check completes)
  useEffect(() => {
    if (!userId || !dbChecked) return;
    const timer = setTimeout(() => {
      if (!isCompleted()) {
        startTour();
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [userId, dbChecked, isCompleted, startTour]);

  return {
    isRunning,
    setIsRunning,
    stepIndex,
    setStepIndex,
    startTour,
    endTour,
    resetTour,
    isCompleted,
  };
};
